import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * The centred dialog the pathway workspace opens things in (the step editor, the
 * encouragement message).
 *
 * It is PORTALED to <body>, never rendered in place. In place, the overlay was a child of
 * the builder's `space-y-*` column: the column's `margin-top` landed on the fixed overlay
 * and pushed it 20px down the viewport, so the top strip of the page (the Super Admin
 * banner, the console header) showed through above the backdrop. The console's theme
 * variables are declared on `:root` (index.css), so nothing is lost by leaving the tree.
 *
 *   - The overlay is the dialog's own scroller and carries `data-lenis-prevent`:
 *     lib/SmoothScroll.jsx runs Lenis on `window`, which otherwise swallows the wheel over
 *     the dialog and scrolls nothing (same convention as components/ui/dialog.jsx).
 *   - The panel is centred with auto margins, not `align-items: center`: a flex-centred
 *     panel taller than the viewport overflows ABOVE the scroller's top edge, where no
 *     scrolling can reach it, and that is where its heading and close button were.
 *   - The dimmed, blurred backdrop is a separate fixed layer, not the scroller itself: a
 *     `backdrop-filter` on the scroller would make it the containing block of every fixed
 *     descendant, so a dialog opened from inside the panel (the media picker) would be
 *     laid out in the scrolled content instead of over the viewport.
 *   - Escape and a press outside the panel call `onClose`; a press inside does not.
 *   - On open, focus moves into the panel (the first field that asks for it with
 *     `autoFocus`, else the panel itself) and returns to the opener on close.
 *   - `role="dialog"` + `aria-modal`, named by `labelledBy`.
 *
 * @param {object}  props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string}  props.labelledBy        id of the element that names the dialog
 * @param {string}  [props.testId]
 * @param {string}  [props.panelClassName]  width / padding of the panel
 * @param {React.ReactNode} props.children
 */
export default function ModalShell({ open, onClose, labelledBy, testId, panelClassName = "", children }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto" data-lenis-prevent="">
      <div className="fixed inset-0 bg-eh-ink/40 backdrop-blur-[2px]" aria-hidden="true" />
      <div
        className="relative flex min-h-full items-start justify-center p-4 sm:p-8"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
          data-testid={testId}
          className={`w-full max-w-lg my-auto bg-white rounded-3xl shadow-2xl border border-eh-border-soft outline-none ${panelClassName}`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
